import { useMemo } from 'react';

import { DEFAULT_TABLE_PAGE_SIZE } from '@/common/components/shared/table/constants';
import {
  useListBusinessUnitsQuery,
  useListCompaniesQuery,
  useListCompanyEmailDomainsQuery,
  useListCountriesQuery,
  useListCurrenciesQuery,
  useListDepartmentsQuery,
  useListDivisionsQuery,
  useListEpmMasterAllocationKeysQuery,
  useListEpmMasterFunctionalitiesQuery,
  useListEpmMasterRoleTypesQuery,
  useListEpmMasterVddsQuery,
  useListHubspotPipelinesQuery,
  useListJobsQuery,
  useListLanguagesQuery,
  useListLocationsQuery,
  useListReasonForLeavingsQuery,
} from '@/common/graphql';
import {
  BusinessUnit,
  Company,
  CompanyEmailDomain,
  Country,
  Currency,
  Department,
  Division,
  EpmMasterAllocationKey,
  EpmMasterFunctionality,
  EpmMasterRoleType,
  EpmMasterVdd,
  HubSpotPipeline,
  Job,
  Language,
  Location,
  ReasonForLeaving,
} from '@/common/types';
import { GeneralType } from '@/common/utils';

export interface GeneralFilter {
  onlyEnabled: boolean;
  keyword: string;
  definitionLanguage: string | null;
}

export interface GetQueryDataResult {
  type: GeneralType;
  items: Array<any>;
  count: number;
  loading: boolean;
}

export const useGetQueryData = (type: GeneralType, page: number, filter: GeneralFilter): GetQueryDataResult => {
  const queryBusinessUnit = useListBusinessUnitsQuery({
    skip: type !== GeneralType.BUSINESS_UNIT,
    variables: {
      pageNumber: page,
      pageSize: DEFAULT_TABLE_PAGE_SIZE,
      keyword: filter.keyword,
      enabled: filter.onlyEnabled ? true : null,
      excludeIds: [],
      companyId: null,
    },
  });
  const queryDivision = useListDivisionsQuery({
    skip: type !== GeneralType.DIVISION,
    variables: {
      pageNumber: page,
      pageSize: DEFAULT_TABLE_PAGE_SIZE,
      keyword: filter.keyword,
      enabled: filter.onlyEnabled ? true : null,
    },
  });
  const queryCompany = useListCompaniesQuery({
    skip: type !== GeneralType.COMPANY,
    variables: {
      pageNumber: page,
      pageSize: DEFAULT_TABLE_PAGE_SIZE,
      enabled: filter.onlyEnabled ? true : null,
    },
  });
  const queryDepartment = useListDepartmentsQuery({
    skip: type !== GeneralType.DEPARTMENT,
    variables: {
      pageNumber: page,
      pageSize: DEFAULT_TABLE_PAGE_SIZE,
      enabled: filter.onlyEnabled ? true : null,
      keyword: filter.keyword,
      excludeIds: [],
      businessUnitId: null,
      companyId: null,
    },
  });
  const queryCompanyEmailDomain = useListCompanyEmailDomainsQuery({
    skip: type !== GeneralType.COMPANY_EMAIL_DOMAIN,
    variables: {
      pageNumber: page,
      pageSize: DEFAULT_TABLE_PAGE_SIZE,
      enabled: filter.onlyEnabled ? true : null,
    },
  });
  const queryJob = useListJobsQuery({
    skip: type !== GeneralType.JOB,
    variables: {
      pageNumber: page,
      pageSize: DEFAULT_TABLE_PAGE_SIZE,
      enabled: filter.onlyEnabled ? true : null,
      keyword: filter.keyword,
    },
  });
  const queryLanguage = useListLanguagesQuery({
    skip: type !== GeneralType.LANGUAGE,
    variables: {
      pageNumber: page,
      pageSize: DEFAULT_TABLE_PAGE_SIZE,
      keyword: filter.keyword,
    },
  });
  const queryLocation = useListLocationsQuery({
    skip: type !== GeneralType.LOCATION,
    variables: {
      pageNumber: page,
      pageSize: DEFAULT_TABLE_PAGE_SIZE,
      enabled: filter.onlyEnabled ? true : null,
      keyword: filter.keyword,
    },
  });
  const queryReasonForLeaving = useListReasonForLeavingsQuery({
    skip: type !== GeneralType.REASON_FOR_LEAVING,
    variables: {
      pageNumber: page,
      pageSize: DEFAULT_TABLE_PAGE_SIZE,
      enabled: filter.onlyEnabled ? true : null,
    },
  });
  const queryHubspotPipeline = useListHubspotPipelinesQuery({
    skip: type !== GeneralType.HUBSPOT_PIPELINE,
    variables: {
      pageNumber: page,
      pageSize: DEFAULT_TABLE_PAGE_SIZE,
      enabled: filter.onlyEnabled ? true : null,
      excludeIds: [],
    },
  });
  const queryCountry = useListCountriesQuery({
    skip: type !== GeneralType.COUNTRY,
    variables: {
      pageNumber: page,
      pageSize: DEFAULT_TABLE_PAGE_SIZE,
      keyword: filter.keyword,
    },
  });
  const queryCurrency = useListCurrenciesQuery({
    skip: type !== GeneralType.CURRENCY,
    variables: {
      pageNumber: page,
      pageSize: DEFAULT_TABLE_PAGE_SIZE,
      keyword: filter.keyword,
    },
  });
  const queryEpmMasterVdd = useListEpmMasterVddsQuery({
    skip: type !== GeneralType.EPM_MASTER_VDD,
    variables: {
      pageNumber: page,
      pageSize: DEFAULT_TABLE_PAGE_SIZE,
      keyword: filter.keyword,
      enabled: filter.onlyEnabled ? true : null,
    },
  });
  const queryEpmMasterFunctionality = useListEpmMasterFunctionalitiesQuery({
    skip: type !== GeneralType.EPM_MASTER_FUNCTIONALITY,
    variables: {
      pageNumber: page,
      pageSize: DEFAULT_TABLE_PAGE_SIZE,
      keyword: filter.keyword,
      enabled: filter.onlyEnabled ? true : null,
    },
  });
  const queryEpmMasterRoleType = useListEpmMasterRoleTypesQuery({
    skip: type !== GeneralType.EPM_MASTER_ROLE_TYPE,
    variables: {
      pageNumber: page,
      pageSize: DEFAULT_TABLE_PAGE_SIZE,
      keyword: filter.keyword,
      enabled: filter.onlyEnabled ? true : null,
    },
  });
  const queryEpmMasterAllocationKey = useListEpmMasterAllocationKeysQuery({
    skip: type !== GeneralType.EPM_MASTER_ALLOCATION_KEY,
    variables: {
      pageNumber: page,
      pageSize: DEFAULT_TABLE_PAGE_SIZE,
      keyword: filter.keyword,
      enabled: filter.onlyEnabled ? true : null,
    },
  });

  const data: {
    count: number | undefined;
    items: Array<any> | undefined;
  } = useMemo(() => {
    switch (type) {
      case GeneralType.BUSINESS_UNIT:
        return {
          count: queryBusinessUnit.data?.listBusinessUnits?.totalCount,
          items: queryBusinessUnit.data?.listBusinessUnits?.items.map(item => BusinessUnit.parse(item)),
        };
      case GeneralType.DIVISION:
        return {
          count: queryDivision.data?.listDivisions?.totalCount,
          items: queryDivision.data?.listDivisions?.items.map(item => Division.parse(item)),
        };
      case GeneralType.COMPANY:
        return {
          count: queryCompany.data?.listCompanies?.totalCount,
          items: queryCompany.data?.listCompanies?.items.map(it => Company.parse(it)),
        };
      case GeneralType.DEPARTMENT:
        return {
          count: queryDepartment.data?.listDepartments?.totalCount,
          items: queryDepartment.data?.listDepartments?.items.map(it => Department.parse(it)),
        };
      case GeneralType.COMPANY_EMAIL_DOMAIN:
        return {
          count: queryCompanyEmailDomain.data?.listCompanyEmailDomains?.totalCount,
          items: queryCompanyEmailDomain.data?.listCompanyEmailDomains?.items.map(it => CompanyEmailDomain.parse(it)),
        };
      case GeneralType.JOB:
        return {
          count: queryJob.data?.listJobs?.totalCount,
          items: queryJob.data?.listJobs?.items.map(it => Job.parse(it)),
        };
      case GeneralType.LANGUAGE:
        return {
          count: queryLanguage.data?.listLanguages?.totalCount,
          items: queryLanguage.data?.listLanguages?.items.map(it => Language.parse(it)),
        };
      case GeneralType.LOCATION:
        return {
          count: queryLocation.data?.listLocations?.totalCount,
          items: queryLocation.data?.listLocations?.items.map(it => Location.parse(it)),
        };
      case GeneralType.REASON_FOR_LEAVING:
        return {
          count: queryReasonForLeaving.data?.listReasonForLeavings?.totalCount,
          items: queryReasonForLeaving.data?.listReasonForLeavings?.items.map(it => ReasonForLeaving.parse(it)),
        };
      case GeneralType.HUBSPOT_PIPELINE:
        return {
          count: queryHubspotPipeline.data?.listHubspotPipelines?.totalCount,
          items: queryHubspotPipeline.data?.listHubspotPipelines?.items.map(it => HubSpotPipeline.parse(it)),
        };
      case GeneralType.COUNTRY:
        return {
          count: queryCountry.data?.listCountries?.totalCount,
          items: queryCountry.data?.listCountries?.items.map(it => Country.parse(it)),
        };
      case GeneralType.CURRENCY:
        return {
          count: queryCurrency.data?.listCurrencies?.totalCount,
          items: queryCurrency.data?.listCurrencies?.items.map(it => Currency.parse(it)),
        };
      case GeneralType.EPM_MASTER_VDD:
        return {
          count: queryEpmMasterVdd.data?.listEpmMasterVdds?.totalCount,
          items: queryEpmMasterVdd.data?.listEpmMasterVdds?.items.map(it => EpmMasterVdd.parse(it)),
        };
      case GeneralType.EPM_MASTER_FUNCTIONALITY:
        return {
          count: queryEpmMasterFunctionality.data?.listEpmMasterFunctionalities?.totalCount,
          items: queryEpmMasterFunctionality.data?.listEpmMasterFunctionalities?.items.map(it =>
            EpmMasterFunctionality.parse(it),
          ),
        };
      case GeneralType.EPM_MASTER_ROLE_TYPE:
        return {
          count: queryEpmMasterRoleType.data?.listEpmMasterRoleTypes?.totalCount,
          items: queryEpmMasterRoleType.data?.listEpmMasterRoleTypes?.items.map(it => EpmMasterRoleType.parse(it)),
        };
      case GeneralType.EPM_MASTER_ALLOCATION_KEY:
        return {
          count: queryEpmMasterAllocationKey.data?.listEpmMasterAllocationKeys?.totalCount,
          items: queryEpmMasterAllocationKey.data?.listEpmMasterAllocationKeys?.items.map(it =>
            EpmMasterAllocationKey.parse(it),
          ),
        };
      default:
        return { count: undefined, items: undefined };
    }
  }, [
    type,
    queryBusinessUnit.data?.listBusinessUnits,
    queryCompany.data?.listCompanies,
    queryCompanyEmailDomain.data?.listCompanyEmailDomains,
    queryCountry.data?.listCountries,
    queryCurrency.data?.listCurrencies,
    queryDepartment.data?.listDepartments,
    queryDivision.data?.listDivisions,
    queryEpmMasterAllocationKey.data?.listEpmMasterAllocationKeys,
    queryEpmMasterFunctionality.data?.listEpmMasterFunctionalities,
    queryEpmMasterRoleType.data?.listEpmMasterRoleTypes,
    queryEpmMasterVdd.data?.listEpmMasterVdds,
    queryHubspotPipeline.data?.listHubspotPipelines,
    queryJob.data?.listJobs,
    queryLanguage.data?.listLanguages,
    queryLocation.data?.listLocations,
    queryReasonForLeaving.data?.listReasonForLeavings,
  ]);

  const loading = useMemo(() => {
    switch (type) {
      case GeneralType.BUSINESS_UNIT:
        return queryBusinessUnit.loading;
      case GeneralType.DIVISION:
        return queryDivision.loading;
      case GeneralType.COMPANY:
        return queryCompany.loading;
      case GeneralType.DEPARTMENT:
        return queryDepartment.loading;
      case GeneralType.COMPANY_EMAIL_DOMAIN:
        return queryCompanyEmailDomain.loading;
      case GeneralType.JOB:
        return queryJob.loading;
      case GeneralType.LANGUAGE:
        return queryLanguage.loading;
      case GeneralType.LOCATION:
        return queryLocation.loading;
      case GeneralType.REASON_FOR_LEAVING:
        return queryReasonForLeaving.loading;
      case GeneralType.HUBSPOT_PIPELINE:
        return queryHubspotPipeline.loading;
      case GeneralType.COUNTRY:
        return queryCountry.loading;
      case GeneralType.CURRENCY:
        return queryCurrency.loading;
      case GeneralType.EPM_MASTER_VDD:
        return queryEpmMasterVdd.loading;
      case GeneralType.EPM_MASTER_FUNCTIONALITY:
        return queryEpmMasterFunctionality.loading;
      case GeneralType.EPM_MASTER_ROLE_TYPE:
        return queryEpmMasterRoleType.loading;
      case GeneralType.EPM_MASTER_ALLOCATION_KEY:
        return queryEpmMasterAllocationKey.loading;
      default:
        return false;
    }
  }, [
    type,
    queryBusinessUnit.loading,
    queryCompany.loading,
    queryCompanyEmailDomain.loading,
    queryCountry.loading,
    queryCurrency.loading,
    queryDepartment.loading,
    queryDivision.loading,
    queryEpmMasterAllocationKey.loading,
    queryEpmMasterFunctionality.loading,
    queryEpmMasterRoleType.loading,
    queryEpmMasterVdd.loading,
    queryHubspotPipeline.loading,
    queryJob.loading,
    queryLanguage.loading,
    queryLocation.loading,
    queryReasonForLeaving.loading,
  ]);

  return { count: data?.count || 0, loading, items: data?.items || [], type };
};
